import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet} from 'react-native';
import {TextInput, Button, Snackbar, Text, IconButton, Divider} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useConfigStore} from '../../store/useConfigStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const WorkingHoursScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [workingDays, setWorkingDays] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('WORKING');
  }, [fetchByType]);

  useEffect(() => {
    setStartTime(getProperty('WORKING', 'BUSINESS_HOURS_START'));
    setEndTime(getProperty('WORKING', 'BUSINESS_HOURS_END'));
    const daysStr = getProperty('WORKING', 'WORKING_DAYS');
    if (daysStr) {
      setWorkingDays(daysStr.split(',').filter(Boolean));
    }
  }, [getProperty, useConfigStore.getState().properties]);

  const toggleDay = useCallback(day => {
    setWorkingDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day],
    );
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProperties('WORKING', [
        {code: 'BUSINESS_HOURS_START', name: 'Business Hours Start', value: startTime},
        {code: 'BUSINESS_HOURS_END', name: 'Business Hours End', value: endTime},
        {code: 'WORKING_DAYS', name: 'Working Days', value: workingDays.join(',')},
      ]);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [startTime, endTime, workingDays, updateProperties, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('config.workingHours')} onBack={() => navigation.goBack()} />

      <ScrollView style={styles.content}>
        <Text variant="labelLarge" style={styles.sectionTitle}>
          {t('config.businessHours').toUpperCase()}
        </Text>
        <Text variant="bodySmall" style={styles.description}>{t('config.businessHoursDesc')}</Text>
        <View style={styles.row}>
          <TextInput
            label={t('config.startTime')}
            value={startTime}
            onChangeText={setStartTime}
            mode="outlined"
            placeholder="09:00"
            style={[styles.input, styles.halfInput]}
          />
          <TextInput
            label={t('config.endTime')}
            value={endTime}
            onChangeText={setEndTime}
            mode="outlined"
            placeholder="21:00"
            style={[styles.input, styles.halfInput]}
          />
        </View>

        <Text variant="labelLarge" style={[styles.sectionTitle, {marginTop: spacing.lg}]}>
          {t('config.workingDays').toUpperCase()}
        </Text>
        <Text variant="bodySmall" style={styles.description}>{t('config.workingDaysDesc')}</Text>

        {DAYS.map((day, index) => {
          const isSelected = workingDays.includes(day);
          return (
            <View key={day}>
              <View style={[styles.dayRow, !isSelected && styles.dayRowDisabled]}>
                <IconButton
                  icon={isSelected ? 'check-circle' : 'circle-outline'}
                  size={24}
                  iconColor={isSelected ? colors.primary : colors.textSecondary}
                  onPress={() => toggleDay(day)}
                  style={styles.toggleBtn}
                />
                <Text style={[styles.dayName, !isSelected && styles.dayNameDisabled]}>
                  {day.charAt(0) + day.slice(1).toLowerCase()}
                </Text>
              </View>
              {index < DAYS.length - 1 && <Divider />}
            </View>
          );
        })}

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving || isLoading}
          style={styles.saveBtn}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {padding: spacing.md},
  sectionTitle: {fontSize: 13, fontWeight: '600', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: spacing.xs},
  description: {color: colors.textSecondary, marginBottom: spacing.sm},
  row: {flexDirection: 'row', gap: spacing.sm},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  halfInput: {flex: 1},
  dayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  dayRowDisabled: {opacity: 0.5},
  toggleBtn: {margin: 0},
  dayName: {fontSize: 16, fontWeight: '600', color: colors.text, flex: 1},
  dayNameDisabled: {color: colors.textSecondary},
  saveBtn: {marginTop: spacing.md, marginBottom: spacing.xxl},
});

export default WorkingHoursScreen;
